import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { FileUpload, FileUploadSelectEvent } from 'primereact/fileupload';
import { Image } from 'primereact/image';

import { SystemConfiguration, AppVersionDetail } from '../../../../../types/cms.types';
import { SystemImageUploadService } from '../../../../../service/supabase/SystemImageUploadService';

interface CpnCreateEditSystemConfigProps {
    visible: boolean;
    onHide: () => void;
    systemConfig?: SystemConfiguration | null;
    onSave: (systemConfigData: any) => Promise<void>;
}

export const CpnCreateEditSystemConfig: React.FC<CpnCreateEditSystemConfigProps> = ({
    visible,
    onHide,
    systemConfig,
    onSave
}) => {
    // Form state
    const [formData, setFormData] = useState({
        app_name: '',
        image_url_banner_app: '',
        terms_of_service_url: '',
        privacy_policy_url: '',
        support_email: '',
        support_phone: '',
        support_fanpage: '',
        support_zalo: '',
        support_group_facebook: '',
        company_name: '',
        company_address: ''
    });

    const [versionDetails, setVersionDetails] = useState<AppVersionDetail[]>([]);
    const [showVersionDialog, setShowVersionDialog] = useState(false);
    const [editingVersion, setEditingVersion] = useState<AppVersionDetail | null>(null);
    const [versionFormData, setVersionFormData] = useState({
        os_type: 'ios' as 'ios' | 'android' | 'web',
        version: '',
        build_number: '',
        required_update_app: false,
        maintenance_mode: false,
        maintenance_message: '',
        release_notes: '',
        download_url: ''
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<any>({});
    const [uploadingBanner, setUploadingBanner] = useState(false);
    const fileUploadBannerRef = useRef<any>(null);

    // OS type options
    const osTypeOptions = [
        { label: 'iOS', value: 'ios', icon: 'pi pi-apple' },
        { label: 'Android', value: 'android', icon: 'pi pi-android' },
        { label: 'Web', value: 'web', icon: 'pi pi-globe' }
    ];

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (visible) {
            if (systemConfig) {
                // Edit mode
                setFormData({
                    app_name: systemConfig.app_name || '',
                    image_url_banner_app: systemConfig.image_url_banner_app || '',
                    terms_of_service_url: systemConfig.terms_of_service_url || '',
                    privacy_policy_url: systemConfig.privacy_policy_url || '',
                    support_email: systemConfig.support_email || '',
                    support_phone: systemConfig.support_phone || '',
                    support_fanpage: systemConfig.support_fanpage || '',
                    support_zalo: systemConfig.support_zalo || '',
                    support_group_facebook: systemConfig.support_group_facebook || '',
                    company_name: systemConfig.company_name || '',
                    company_address: systemConfig.company_address || ''
                });
                setVersionDetails(systemConfig.version_details || []);
            } else {
                // Create mode
                setFormData({
                    app_name: 'LMD - Gọi Xe',
                    image_url_banner_app: '',
                    terms_of_service_url: '',
                    privacy_policy_url: '',
                    support_email: '',
                    support_phone: '',
                    support_fanpage: '',
                    support_zalo: '',
                    support_group_facebook: '',
                    company_name: '',
                    company_address: ''
                });
                setVersionDetails([
                    {
                        os_type: 'ios',
                        version: '1.0.0',
                        build_number: '1',
                        required_update_app: false,
                        maintenance_mode: false,
                        maintenance_message: '',
                        release_notes: '',
                        download_url: ''
                    },
                    {
                        os_type: 'android',
                        version: '1.0.0',
                        build_number: '1',
                        required_update_app: false,
                        maintenance_mode: false,
                        maintenance_message: '',
                        release_notes: '',
                        download_url: ''
                    }
                ]);
            }
            setErrors({});
        }
    }, [visible, systemConfig]);

    // Handle input changes
    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user types
        if (errors[field]) {
            setErrors((prev: any) => ({
                ...prev,
                [field]: null
            }));
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors: any = {};

        if (!formData.app_name.trim()) {
            newErrors.app_name = 'Tên ứng dụng là bắt buộc';
        }

        if (versionDetails.length === 0) {
            newErrors.version_details = 'Cần có ít nhất một phiên bản';
        }

        // Validate version details
        versionDetails.forEach((version, index) => {
            if (!version.version.trim()) {
                newErrors[`version_${index}_version`] = 'Số phiên bản là bắt buộc';
            }
            if (!version.os_type) {
                newErrors[`version_${index}_os_type`] = 'Hệ điều hành là bắt buộc';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle banner upload
    const handleBannerSelect = async (e: FileUploadSelectEvent) => {
        const file = e.files[0];
        if (!file) return;

        setUploadingBanner(true);
        const { url, error } = await SystemImageUploadService.uploadImage(file, 'other');
        setUploadingBanner(false);

        if (error) {
            setErrors((prev: any) => ({ ...prev, image_url_banner_app: error }));
        } else if (url) {
            setFormData(prev => ({ ...prev, image_url_banner_app: url }));
            if (errors.image_url_banner_app) {
                setErrors((prev: any) => ({ ...prev, image_url_banner_app: '' }));
            }
        }

        // Clear the file upload
        if (fileUploadBannerRef.current) {
            fileUploadBannerRef.current.clear();
        }
    };

    // Handle submit
    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);

            const systemConfigData = {
                ...formData,
                version_details: versionDetails,
                ...(systemConfig?.id && { id: systemConfig.id })
            };

            await onSave(systemConfigData);
            onHide();
        } catch (error) {
            console.error('Error saving system config:', error);
        } finally {
            setLoading(false);
        }
    };

    // Version detail handlers
    const handleAddVersion = () => {
        setEditingVersion(null);
        setVersionFormData({
            os_type: 'ios',
            version: '',
            build_number: '',
            required_update_app: false,
            maintenance_mode: false,
            maintenance_message: '',
            release_notes: '',
            download_url: ''
        });
        setShowVersionDialog(true);
    };

    const handleEditVersion = (version: AppVersionDetail, index: number) => {
        setEditingVersion({ ...version, index } as any);
        setVersionFormData({
            os_type: version.os_type,
            version: version.version,
            build_number: version.build_number || '',
            required_update_app: version.required_update_app || false,
            maintenance_mode: version.maintenance_mode || false,
            maintenance_message: version.maintenance_message || '',
            release_notes: version.release_notes || '',
            download_url: version.download_url || ''
        });
        setShowVersionDialog(true);
    };

    const handleDeleteVersion = (index: number) => {
        const newVersions = versionDetails.filter((_, i) => i !== index);
        setVersionDetails(newVersions);
    };

    const handleSaveVersion = () => {
        if (!versionFormData.version.trim()) return;

        if (editingVersion && 'index' in editingVersion) {
            // Edit existing
            const newVersions = [...versionDetails];
            newVersions[editingVersion.index as any] = { ...versionFormData };
            setVersionDetails(newVersions);
        } else {
            // Add new
            setVersionDetails(prev => [...prev, { ...versionFormData }]);
        }

        setShowVersionDialog(false);
    };

    // Column templates
    const osTypeTemplate = (version: AppVersionDetail) => {
        const osOption = osTypeOptions.find(opt => opt.value === version.os_type);
        return (
            <div className="flex items-center gap-2">
                <i className={osOption?.icon} />
                <span>{osOption?.label}</span>
            </div>
        );
    };

    const versionStatusTemplate = (version: AppVersionDetail) => (
        <div className="flex flex-wrap gap-1">
            {version.maintenance_mode && (
                <Tag value="Bảo trì" severity="danger" className="text-xs" />
            )}
            {version.required_update_app && (
                <Tag value="Buộc nâng cấp" severity="warning" className="text-xs" />
            )}
            {!version.maintenance_mode && !version.required_update_app && (
                <Tag value="Bình thường" severity="success" className="text-xs" />
            )}
        </div>
    );

    const actionTemplate = (version: AppVersionDetail, options: any) => (
        <div className="flex gap-1">
            <Button
                icon="pi pi-pencil"
                className="p-button-text p-button-warning p-button-sm"
                tooltip="Chỉnh sửa"
                onClick={() => handleEditVersion(version, options.rowIndex)}
            />
            <Button
                icon="pi pi-trash"
                className="p-button-text p-button-danger p-button-sm"
                tooltip="Xóa"
                onClick={() => handleDeleteVersion(options.rowIndex)}
            />
        </div>
    );

    // Dialog footer
    const dialogFooter = (
        <div>
            <Button
                label="Hủy"
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-text"
                disabled={loading}
            />
            <Button
                label={systemConfig ? 'Cập nhật' : 'Tạo mới'}
                icon="pi pi-check"
                onClick={handleSubmit}
                loading={loading}
            />
        </div>
    );

    const versionDialogFooter = (
        <div>
            <Button
                label="Hủy"
                icon="pi pi-times"
                onClick={() => setShowVersionDialog(false)}
                className="p-button-text"
            />
            <Button
                label="Lưu"
                icon="pi pi-check"
                onClick={handleSaveVersion}
                disabled={!versionFormData.version.trim()}
            />
        </div>
    );

    return (
        <>
            {/* Main Dialog */}
            <Dialog
                header={systemConfig ? 'Chỉnh sửa cấu hình hệ thống' : 'Tạo cấu hình hệ thống mới'}
                visible={visible}
                onHide={onHide}
                style={{ width: '900px' }}
                maximizable
                modal
                footer={dialogFooter}
            >
                <div className="grid">
                    {/* Basic Information */}
                    <div className="col-12">
                        <h5>Thông tin cơ bản</h5>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="app_name" className="font-semibold">Tên ứng dụng *</label>
                            <InputText
                                id="app_name"
                                value={formData.app_name}
                                onChange={(e) => handleInputChange('app_name', e.target.value)}
                                className={`w-full ${errors.app_name ? 'p-invalid' : ''}`}
                                placeholder="Nhập tên ứng dụng"
                            />
                            {errors.app_name && (
                                <small className="p-error">{errors.app_name}</small>
                            )}
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="company_name" className="font-semibold">Tên công ty</label>
                            <InputText
                                id="company_name"
                                value={formData.company_name}
                                onChange={(e) => handleInputChange('company_name', e.target.value)}
                                className="w-full"
                                placeholder="Nhập tên công ty"
                            />
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="image_url_banner_app" className="font-semibold">Banner ứng dụng</label>
                            <div className="flex gap-2 align-items-center mb-2">
                                <FileUpload
                                    ref={fileUploadBannerRef}
                                    mode="basic"
                                    name="banner"
                                    accept="image/*"
                                    maxFileSize={5000000}
                                    onSelect={handleBannerSelect}
                                    auto={false}
                                    chooseLabel="Chọn ảnh banner"
                                    disabled={loading || uploadingBanner}
                                    className="flex-1"
                                />
                                {uploadingBanner && <i className="pi pi-spin pi-spinner" />}
                            </div>
                            <InputText
                                id="image_url_banner_app"
                                value={formData.image_url_banner_app}
                                onChange={(e) => handleInputChange('image_url_banner_app', e.target.value)}
                                className={`w-full ${errors.image_url_banner_app ? 'p-invalid' : ''}`}
                                placeholder="Hoặc nhập URL banner trực tiếp"
                                disabled={loading}
                            />
                            {errors.image_url_banner_app && <small className="p-error">{errors.image_url_banner_app}</small>}
                            {formData.image_url_banner_app && (
                                <div className="mt-2">
                                    <Image src={formData.image_url_banner_app} alt="Banner Preview" width="300" preview />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="company_address" className="font-semibold">Địa chỉ công ty</label>
                            <InputTextarea
                                id="company_address"
                                value={formData.company_address}
                                onChange={(e) => handleInputChange('company_address', e.target.value)}
                                className="w-full"
                                rows={2}
                                placeholder="Nhập địa chỉ công ty"
                            />
                        </div>
                    </div>

                    {/* Support Information */}
                    <div className="col-12">
                        <h5>Thông tin hỗ trợ</h5>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="company_name" className="font-semibold">Tên công ty</label>
                            <InputText
                                id="company_name"
                                value={formData.company_name}
                                onChange={(e) => handleInputChange('company_name', e.target.value)}
                                className="w-full"
                                placeholder="Nhập tên công ty"
                            />
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="company_address" className="font-semibold">Địa chỉ công ty</label>
                            <InputTextarea
                                id="company_address"
                                value={formData.company_address}
                                onChange={(e) => handleInputChange('company_address', e.target.value)}
                                className="w-full"
                                rows={2}
                                placeholder="Nhập địa chỉ công ty"
                            />
                        </div>
                    </div>

                    {/* Support Information */}
                    <div className="col-12">
                        <h5>Thông tin hỗ trợ</h5>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="support_email" className="font-semibold">Email hỗ trợ</label>
                            <InputText
                                id="support_email"
                                value={formData.support_email}
                                onChange={(e) => handleInputChange('support_email', e.target.value)}
                                className="w-full"
                                placeholder="Nhập email hỗ trợ"
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="support_phone" className="font-semibold">Số điện thoại hỗ trợ</label>
                            <InputText
                                id="support_phone"
                                value={formData.support_phone}
                                onChange={(e) => handleInputChange('support_phone', e.target.value)}
                                className="w-full"
                                placeholder="Nhập số điện thoại hỗ trợ"
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="support_fanpage" className="font-semibold">Fanpage Facebook</label>
                            <InputText
                                id="support_fanpage"
                                value={formData.support_fanpage}
                                onChange={(e) => handleInputChange('support_fanpage', e.target.value)}
                                className="w-full"
                                placeholder="Nhập URL Fanpage Facebook"
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="support_zalo" className="font-semibold">Zalo hỗ trợ</label>
                            <InputText
                                id="support_zalo"
                                value={formData.support_zalo}
                                onChange={(e) => handleInputChange('support_zalo', e.target.value)}
                                className="w-full"
                                placeholder="Nhập URL Zalo hỗ trợ"
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="support_group_facebook" className="font-semibold">Group Facebook</label>
                            <InputText
                                id="support_group_facebook"
                                value={formData.support_group_facebook}
                                onChange={(e) => handleInputChange('support_group_facebook', e.target.value)}
                                className="w-full"
                                placeholder="Nhập URL Group Facebook"
                            />
                        </div>
                    </div>

                    {/* Legal Links */}
                    <div className="col-12">
                        <h5>Liên kết pháp lý</h5>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="terms_of_service_url" className="font-semibold">URL Điều khoản dịch vụ</label>
                            <InputText
                                id="terms_of_service_url"
                                value={formData.terms_of_service_url}
                                onChange={(e) => handleInputChange('terms_of_service_url', e.target.value)}
                                className="w-full"
                                placeholder="Nhập URL điều khoản dịch vụ"
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="privacy_policy_url" className="font-semibold">URL Chính sách bảo mật</label>
                            <InputText
                                id="privacy_policy_url"
                                value={formData.privacy_policy_url}
                                onChange={(e) => handleInputChange('privacy_policy_url', e.target.value)}
                                className="w-full"
                                placeholder="Nhập URL chính sách bảo mật"
                            />
                        </div>
                    </div>



                    {/* Version Details */}
                    <div className="col-12">
                        <div className="flex align-items-center justify-content-between">
                            <h5>Chi tiết phiên bản</h5>
                            <Button
                                label="Thêm phiên bản"
                                icon="pi pi-plus"
                                onClick={handleAddVersion}
                                className="p-button-sm"
                            />
                        </div>
                    </div>

                    <div className="col-12">
                        {errors.version_details && (
                            <small className="p-error">{errors.version_details}</small>
                        )}
                        <DataTable
                            value={versionDetails}
                            emptyMessage="Chưa có phiên bản nào"
                            size="small"
                            className="mt-2"
                            scrollable
                            scrollHeight="300px"
                        >
                            <Column
                                field="os_type"
                                header="Hệ điều hành"
                                body={osTypeTemplate}
                                style={{ width: '120px' }}
                            />
                            <Column
                                field="version"
                                header="Phiên bản"
                                style={{ width: '100px' }}
                            />
                            <Column
                                field="build_number"
                                header="Build"
                                style={{ width: '80px' }}
                            />
                            <Column
                                header="Trạng thái"
                                body={versionStatusTemplate}
                                style={{ width: '150px' }}
                            />
                            <Column
                                header="Thao tác"
                                body={actionTemplate}
                                style={{ width: '100px' }}
                            />
                        </DataTable>
                    </div>
                </div>
            </Dialog>

            {/* Version Detail Dialog */}
            <Dialog
                header={editingVersion ? 'Chỉnh sửa phiên bản' : 'Thêm phiên bản mới'}
                visible={showVersionDialog}
                onHide={() => setShowVersionDialog(false)}
                style={{ width: '600px' }}
                modal
                footer={versionDialogFooter}
            >
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="os_type" className="font-semibold">Hệ điều hành *</label>
                            <Dropdown
                                id="os_type"
                                value={versionFormData.os_type}
                                options={osTypeOptions}
                                onChange={(e) => setVersionFormData(prev => ({ ...prev, os_type: e.value }))}
                                className="w-full"
                                placeholder="Chọn hệ điều hành"
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="version" className="font-semibold">Phiên bản *</label>
                            <InputText
                                id="version"
                                value={versionFormData.version}
                                onChange={(e) => setVersionFormData(prev => ({ ...prev, version: e.target.value }))}
                                className="w-full"
                                placeholder="Ví dụ: 1.0.0"
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="build_number" className="font-semibold">Build Number</label>
                            <InputText
                                id="build_number"
                                value={versionFormData.build_number}
                                onChange={(e) => setVersionFormData(prev => ({ ...prev, build_number: e.target.value }))}
                                className="w-full"
                                placeholder="Ví dụ: 1"
                            />
                        </div>
                    </div>



                    <div className="col-12 md:col-6">
                        <div className="field">
                            <div className="flex align-items-center gap-2">
                                <InputSwitch
                                    id="required_update_app"
                                    checked={versionFormData.required_update_app}
                                    onChange={(e) => setVersionFormData(prev => ({ ...prev, required_update_app: !!e.value }))}
                                />
                                <label htmlFor="required_update_app" className="font-semibold">
                                    Buộc nâng cấp app
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <div className="flex align-items-center gap-2">
                                <InputSwitch
                                    id="maintenance_mode_version"
                                    checked={versionFormData.maintenance_mode}
                                    onChange={(e) => setVersionFormData(prev => ({ ...prev, maintenance_mode: !!e.value }))}
                                />
                                <label htmlFor="maintenance_mode_version" className="font-semibold">
                                    Chế độ bảo trì
                                </label>
                            </div>
                        </div>
                    </div>

                    {versionFormData.maintenance_mode && (
                        <div className="col-12">
                            <div className="field">
                                <label htmlFor="maintenance_message_version" className="font-semibold">Thông báo bảo trì</label>
                                <InputTextarea
                                    id="maintenance_message_version"
                                    value={versionFormData.maintenance_message}
                                    onChange={(e) => setVersionFormData(prev => ({ ...prev, maintenance_message: e.target.value }))}
                                    className="w-full"
                                    rows={3}
                                    placeholder="Nhập thông báo bảo trì cho phiên bản này"
                                />
                            </div>
                        </div>
                    )}

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="download_url" className="font-semibold">URL tải về</label>
                            <InputText
                                id="download_url"
                                value={versionFormData.download_url}
                                onChange={(e) => setVersionFormData(prev => ({ ...prev, download_url: e.target.value }))}
                                className="w-full"
                                placeholder="Nhập URL tải về"
                            />
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="release_notes" className="font-semibold">Ghi chú phát hành</label>
                            <InputTextarea
                                id="release_notes"
                                value={versionFormData.release_notes}
                                onChange={(e) => setVersionFormData(prev => ({ ...prev, release_notes: e.target.value }))}
                                className="w-full"
                                rows={4}
                                placeholder="Nhập ghi chú phát hành"
                            />
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};